import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Button,
  Pagination,
  Grid,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import apiClient from '../../../../api/client';

const UserSessionLog = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    page: 1
  });
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    startDate: '',
    endDate: '',
    accountId: '',
    branchId: ''
  });

  // Active user count
  const [activeUserCount, setActiveUserCount] = useState(0);

  // Fetch sessions
  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Enhanced debugging for authentication issues
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }
      
      const params = {
        ...filters,
        limit: pagination.limit,
        offset: pagination.offset
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      console.log('🔍 Fetching sessions with params:', params);
      const response = await apiClient.get('/api/v1/sessions', { params });
      
      console.log('✅ Sessions response:', response.data);
      setSessions(response.data.sessions || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0
      }));
    } catch (error) {
      console.error('❌ Error fetching sessions:', error);
      
      // Enhanced error handling
      if (error.response?.status === 401) {
        setError('Authentication expired. Please log in again.');
      } else if (error.response?.status === 403) {
        setError('Access denied. You do not have permission to view user sessions.');
      } else if (error.response?.status === 400) {
        setError('Invalid request. Please check your account settings.');
      } else if (error.response?.data?.message) {
        setError(`Server error: ${error.response.data.message}`);
      } else if (error.message?.includes('Network Error')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('Failed to load user sessions. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch active user count
  const fetchActiveUserCount = async () => {
    try {
      const response = await apiClient.get('/api/v1/sessions/active-count');
      setActiveUserCount(response.data.activeUsers || 0);
    } catch (error) {
      console.error('Error fetching active user count:', error);
    }
  };

  useEffect(() => {
    fetchSessions();
    fetchActiveUserCount();
  }, [pagination.offset, pagination.limit]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Apply filters
  const applyFilters = () => {
    setPagination(prev => ({ ...prev, offset: 0, page: 1 }));
    fetchSessions();
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      status: '',
      startDate: '',
      endDate: '',
      accountId: '',
      branchId: ''
    });
    setPagination(prev => ({ ...prev, offset: 0, page: 1 }));
    setTimeout(() => fetchSessions(), 100);
  };

  // Export to CSV
  const handleExport = async () => {
    try {
      const params = { ...filters };
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await apiClient.get('/api/v1/sessions/export', {
        params,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `user-sessions-${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting sessions:', error);
      setError('Failed to export sessions. Please try again.');
    }
  };

  // Handle pagination change
  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage,
      offset: (newPage - 1) * prev.limit
    }));
  };

  // Status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'ended': return 'default';
      case 'timed_out': return 'warning';
      default: return 'default';
    }
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        User Activity Log
      </Typography>

      {/* Active Users Count */}
      <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <CardContent>
          <Typography variant="h6">
            Currently Active Users: {activeUserCount}
          </Typography>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="ended">Ended</MenuItem>
                  <MenuItem value="timed_out">Timed Out</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="contained"
                onClick={applyFilters}
                startIcon={<FilterIcon />}
                fullWidth
                disabled={loading}
              >
                Apply Filters
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                onClick={clearFilters}
                fullWidth
                disabled={loading}
              >
                Clear Filters
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                onClick={handleExport}
                startIcon={<DownloadIcon />}
                fullWidth
                disabled={loading}
              >
                Export CSV
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <IconButton 
                onClick={() => {
                  fetchSessions();
                  fetchActiveUserCount();
                }} 
                color="primary"
                disabled={loading}
              >
                <RefreshIcon />
              </IconButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Sessions Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            User Sessions ({pagination.total} total)
          </Typography>
          
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Branch</TableCell>
                      <TableCell>Session Start</TableCell>
                      <TableCell>Session End</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>IP Address</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          No sessions found
                        </TableCell>
                      </TableRow>
                    ) : (
                      sessions.map((session) => (
                        <TableRow key={session.session_id}>
                          <TableCell>
                            {session.full_name || session.username || 'Unknown'}
                          </TableCell>
                          <TableCell>{session.email || '-'}</TableCell>
                          <TableCell>{session.role_name || '-'}</TableCell>
                          <TableCell>{session.branch_name || '-'}</TableCell>
                          <TableCell>
                            {formatDate(session.session_start)}
                          </TableCell>
                          <TableCell>
                            {formatDate(session.session_end)}
                          </TableCell>
                          <TableCell>
                            {formatDuration(session.duration_seconds)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={session.status || 'unknown'}
                              color={getStatusColor(session.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{session.ip_address || '-'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {pagination.total > pagination.limit && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={Math.ceil(pagination.total / pagination.limit)}
                    page={pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserSessionLog;